import { useEffect, useMemo, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import { refetchQueries } from './refetchQueries';
import {
  IResponseData,
  IError,
  IApiParams,
  IPlant,
  IChangePlantNotificationStateRequest,
  IPlantTemperature,
  IPlantTemperatureRequest,
} from 'types';
import { useAuth, useError } from '../providers';
import { useAuthClient } from './useAuthClient';
import { firebaseAnalytics } from '../analytics';
import { errors } from '../config';

const plantsKey = 'getPlants';
const getPlantMixTemperatureKey = 'getPlantMixTemperatureKey';

const defaultSkip = 0;

export function usePlantApi({
  queryParameters,
  organizationId,
  isInfiniteScrolling = true,
}: IApiParams) {
  const authClient = useAuthClient();
  const { openModal } = useError();
  const { supplierId } = useAuth();
  const queryClient = useQueryClient();
  const [plants, setPlants] = useState<IPlant[]>([]);

  const currentSupplierId = useMemo(() => {
    return organizationId || supplierId;
  }, [organizationId, supplierId]);

  const getPlantsUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/get-plants`;
  }, [currentSupplierId]);

  const changePlantNotificationsStateUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/change-enable-plants`;
  }, [currentSupplierId]);

  const getPlantMixTemperatureUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/get-plant-mix-temperature`;
  }, [currentSupplierId]);

  const setPlantMixTemperatureUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Plant/set-plant-mix-temperature`;
  }, [currentSupplierId]);

  const {
    data: plantsResponse,
    refetch: fetchPlants,
    isFetching: isFetchingPlants,
    isFetched,
  } = useQueryWithErrorHandler<IResponseData<IPlant[]>, IError>({
    queryKey: [plantsKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getPlantsUrl}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(errors.getPlantsList, error, {
        getPlantsUrl,
        ...queryParameters,
      });
      openModal(error);
    },

    placeholderData: keepPreviousData,
  });

  const {
    data: getPlantMixTemperatureResponse,
    refetch: fetchPlantMixTemperatureResponse,
    isFetching: isFetchingPlantMixTemperatureResponse,
  } = useQueryWithErrorHandler<IResponseData<IPlantTemperature>, IError>({
    queryKey: [getPlantMixTemperatureKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getPlantMixTemperatureUrl}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      openModal(error);
    },

    placeholderData: keepPreviousData,
  });

  const { mutateAsync: changePlantMixTemperature } = useMutation({
    mutationFn: (payload: IPlantTemperatureRequest) =>
      authClient(setPlantMixTemperatureUrl, {
        method: 'PUT',
        data: payload,
        queryParameters,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [getPlantMixTemperatureKey],
      });
      return queryClient.getQueryData<boolean>([getPlantMixTemperatureKey]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<boolean>([getPlantMixTemperatureKey], context);
      }

      openModal(error);
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [getPlantMixTemperatureKey],
      });
      await refetchQueries(queryClient, {
        queryKey: [getPlantMixTemperatureKey],
      });
    },
  });

  const {
    mutateAsync: changePlantNotificationState,
    isPending: isChangingPlantNotificationState,
    isSuccess: isChangingPlantNotificationStateSuccess,
    error: changePlantNotificationStateError,
  } = useMutation({
    mutationFn: (payload: IChangePlantNotificationStateRequest[]) =>
      authClient(changePlantNotificationsStateUrl, {
        method: 'PATCH',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [plantsKey],
      });
      return queryClient.getQueryData<boolean>([plantsKey]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<boolean>([plantsKey], context);
      }

      firebaseAnalytics.logErrorEvent(
        errors.changePlantNotificationState,
        error,
        {
          changePlantNotificationsStateUrl,
        },
      );
      openModal(error);
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [plantsKey],
      });
      await refetchQueries(queryClient, {
        queryKey: [plantsKey],
      });
    },
  });

  useEffect(() => {
    if (!isFetchingPlants) {
      setPlants((prevState: IPlant[]) => {
        return queryParameters?.skip !== defaultSkip && isInfiniteScrolling
          ? plantsResponse?.data
            ? [...prevState, ...plantsResponse.data]
            : prevState
          : plantsResponse?.data || [];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantsResponse?.data]);

  return {
    plantsResponse,
    fetchPlants,
    isFetchingPlants,
    plants,
    isChangingPlantNotificationState,
    isChangingPlantNotificationStateSuccess,
    changePlantNotificationStateError,
    changePlantNotificationState,
    isFetched,
    getPlantMixTemperatureResponse,
    fetchPlantMixTemperatureResponse,
    isFetchingPlantMixTemperatureResponse,
    changePlantMixTemperature,
  };
}
